/**
 * DataCacheContext
 * ----------------
 * Sayfalar arası paylaşımlı in-memory cache. Nadiren değişen referans veriler
 * (products, product-colors, customers) sadece bir kez çekilir ve her sayfada
 * anında kullanılabilir. 60 saniyeden eski entry'ler arka planda sessizce
 * yenilenir (stale-while-revalidate).
 *
 * Kullanım:
 *   const { products, colors, customers, refresh } = useDataCache();
 */
import { createContext, useContext, useRef, useState, useCallback, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const DataCacheContext = createContext(null);

const STALE_MS = 60 * 1000; // 60s

export function DataCacheProvider({ children }) {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [colors, setColors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cuttingTasks, setCuttingTasks] = useState([]);
  const [blacklist, setBlacklist] = useState([]);
  const stamps = useRef({
    products: 0, colors: 0, customers: 0, cuttingTasks: 0, blacklist: 0,
  });
  const inflight = useRef({});

  const fetchKey = useCallback(async (key, url, setter) => {
    if (inflight.current[key]) return inflight.current[key];
    inflight.current[key] = api
      .get(url)
      .then((r) => {
        setter(r.data || []);
        stamps.current[key] = Date.now();
      })
      .catch((e) => {
        if (key === "cuttingTasks") toast.error("Kesim görevleri yüklenemedi, sayfayı yenileyin");
        console.error(`cache ${key} failed`, e);
      })
      .finally(() => {
        inflight.current[key] = null;
      });
    return inflight.current[key];
  }, []);

  const ensure = useCallback(
    async (key) => {
      const age = Date.now() - (stamps.current[key] || 0);
      if (age < STALE_MS) return; // fresh enough
      if (key === "products") await fetchKey("products", "/products", setProducts);
      else if (key === "colors") await fetchKey("colors", "/product-colors", setColors);
      else if (key === "customers") await fetchKey("customers", "/customers", setCustomers);
      else if (key === "cuttingTasks") await fetchKey("cuttingTasks", "/cutting-tasks", setCuttingTasks);
      else if (key === "blacklist") await fetchKey("blacklist", "/blacklist", setBlacklist);
    },
    [fetchKey]
  );

  const refresh = useCallback(
    async (key) => {
      stamps.current[key] = 0;
      await ensure(key);
    },
    [ensure]
  );

  // Kullanıcı değişince (login/logout) cache sıfırlansın
  useEffect(() => {
    if (!user) {
      setProducts([]);
      setColors([]);
      setCustomers([]);
      setCuttingTasks([]);
      setBlacklist([]);
      stamps.current = { products: 0, colors: 0, customers: 0, cuttingTasks: 0, blacklist: 0 };
      return;
    }
    // Oturum açılınca referansları önden yükle (parallel)
    Promise.all([
      ensure("products"),
      ensure("colors"),
      ensure("customers"),
      ensure("cuttingTasks"),
      ensure("blacklist"),
    ]);
  }, [user, ensure]);

  // WS 'rt:cutting_task' sinyali gelince cuttingTasks'ı yenile.
  useEffect(() => {
    if (!user) return;
    const onCt = () => refresh("cuttingTasks");
    const onBl = () => refresh("blacklist");
    const onProd = () => refresh("products");
    const onCol = () => refresh("colors");
    const onCust = () => refresh("customers");
    window.addEventListener("rt:cutting_task", onCt);
    window.addEventListener("rt:blacklist_customer", onBl);
    window.addEventListener("rt:product", onProd);
    window.addEventListener("rt:product_color", onCol);
    window.addEventListener("rt:customer", onCust);
    return () => {
      window.removeEventListener("rt:cutting_task", onCt);
      window.removeEventListener("rt:blacklist_customer", onBl);
      window.removeEventListener("rt:product", onProd);
      window.removeEventListener("rt:product_color", onCol);
      window.removeEventListener("rt:customer", onCust);
    };
  }, [user, refresh]);

  const value = {
    products,
    colors,
    customers,
    cuttingTasks,
    blacklist,
    ensure,
    refresh,
  };
  return <DataCacheContext.Provider value={value}>{children}</DataCacheContext.Provider>;
}

export function useDataCache() {
  const ctx = useContext(DataCacheContext);
  if (!ctx) throw new Error("useDataCache must be used within DataCacheProvider");
  return ctx;
}
